import React from "react";
import { FaCreditCard, FaUndo } from "react-icons/fa";
import { TextInput, NumberInput, Checkbox } from "../../../atoms";
import { SettingsSubgroupHeader } from "./SettingsSectionShell";
import valuesEqual from "./hooks/valuesEqual";
import { formatTl } from "./paymentFormat";
// getTokens/FilterTabs "teal"i desteklemiyor (sadece red/indigo/blue/amber/
// emerald). Ödeme bölümü için emerald seçildi (yeşil = para/ödeme anlamı).
const COLOR = "emerald";
const PRICE_KEYS = new Set([
  "turkishCoffeeSmallPrice",
  "turkishCoffeeMediumPrice",
  "teaBrewPrice",
  "filterCoffeeBrewPrice",
]);
// #253: bayinerpayQr / bayinerpayNfc yeni makinelerin anahtarı; qr / bayinerpay
// henüz güncellenmemiş makinelerden gelir. NearPay / PAX şimdilik gösterilmez.
const ORDER = [
  "bayinerpayQr",
  "bayinerpayNfc",
  "qr",
  "bayinerpay",
  "turkishCoffeeSmallPrice",
  "turkishCoffeeMediumPrice",
  "teaBrewPrice",
  "filterCoffeeBrewPrice",
];

const FieldShell = ({ label, isChanged, onRevert, children }) => (
  <div
    className={[
      "relative rounded-xl border bg-white p-3 transition-all duration-200",
      isChanged
        ? "border-primary ring-2 ring-primary/20 bg-primary/5"
        : "border-gray-200 hover:border-primary",
    ].join(" ")}
  >
    <div className="flex items-start justify-between gap-2 mb-2">
      <label
        className="text-xs font-semibold text-gray-700 leading-tight truncate"
        title={label}
      >
        {label}
      </label>
      {isChanged && (
        <div className="flex items-center gap-1 shrink-0">
          {onRevert && (
            <button
              type="button"
              onClick={onRevert}
              className="text-gray-400 hover:text-primary transition"
              title="Geri al"
            >
              <FaUndo size={10} />
            </button>
          )}
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-bold bg-primary/20 text-main">
            YENİ
          </span>
        </div>
      )}
    </div>
    {children}
  </div>
);

/**
 * Ödeme — artık kendi sekmesi yok, "Sistem" bölümünün alt grubu olarak
 * ProfileSettingsSection içinde render edilir. Yazma grubu değişmedi: `payment`.
 */
export default function PaymentSection({
  data,
  onChange,
  originalData,
  showHeader = true,
}) {
  if (!data || Object.keys(data).length === 0) return null;
  const emit = (key, value) => onChange({ group: "payment", key, value });
  const valueOf = (key) => data?.[key]?.value;
  const origOf = (key) => originalData?.[key]?.value;
  const changed = (key) => {
    const o = origOf(key);
    return o !== undefined && !valuesEqual(valueOf(key), o);
  };
  const keys = ORDER.filter((k) => k in data);

  return (
    <div>
      {showHeader && (
        <SettingsSubgroupHeader
          icon={FaCreditCard}
          title="Ödeme"
          count={keys.length}
          color={COLOR}
        />
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {keys.map((k) => {
          const meta = data[k];
          if (k === "nearpayTerminalId" && !valueOf("nearpay")) return null;
          if (k === "paxTerminalId" && !valueOf("pax")) return null;

          if (meta.type === "Checkbox") {
            return (
              <FieldShell
                key={k}
                label={meta.label}
                isChanged={changed(k)}
                onRevert={changed(k) ? () => emit(k, origOf(k)) : undefined}
              >
                <Checkbox
                  checked={!!meta.value}
                  onChange={(e) => emit(k, !!e.target.checked)}
                  activeLabel="Açık"
                  inactiveLabel="Kapalı"
                />
                {changed(k) && origOf(k) !== undefined && (
                  <p className="text-[10px] text-primary mt-1">
                    Önceki: {origOf(k) ? "Açık" : "Kapalı"}
                  </p>
                )}
              </FieldShell>
            );
          }
          if (meta.type === "Input") {
            return (
              <FieldShell
                key={k}
                label={meta.label}
                isChanged={changed(k)}
                onRevert={changed(k) ? () => emit(k, origOf(k)) : undefined}
              >
                <TextInput
                  value={meta.value || ""}
                  onChange={(e) => emit(k, e?.target?.value ?? "")}
                  className="!py-1.5 !text-sm !rounded-lg !px-3 !shadow-none"
                  wrapperClassName="!space-y-0"
                />
                {changed(k) && origOf(k) !== undefined && (
                  <p className="text-[10px] text-primary mt-1">
                    Önceki: {origOf(k) || "(boş)"}
                  </p>
                )}
              </FieldShell>
            );
          }
          if (meta.type === "Number" && PRICE_KEYS.has(k)) {
            const kurus = Number(meta.value) || 0;
            const max = meta.upperBound ?? 100000;
            const overMax = kurus > max;
            const underMin = kurus < (meta.lowerBound ?? 0);
            const notInteger = !Number.isInteger(kurus);
            const hasError = overMax || underMin || notInteger;
            return (
              <FieldShell
                key={k}
                label={`${meta.label} (kuruş)`}
                isChanged={changed(k)}
                onRevert={changed(k) ? () => emit(k, origOf(k)) : undefined}
              >
                <NumberInput
                  value={kurus}
                  onChange={(v) => {
                    // Sadece tam sayı kuruş — virgül/ondalık kabul edilmez.
                    const n = v === "" ? 0 : Math.trunc(Number(v));
                    emit(k, Number.isNaN(n) ? 0 : Math.max(0, n));
                  }}
                  min={0}
                  max={max}
                  step={1}
                  error={hasError}
                  errorMessage={
                    overMax
                      ? "En fazla 100000 kuruş (1.000 ₺) olabilir"
                      : underMin
                        ? "Negatif olamaz"
                        : notInteger
                          ? "Sadece tam sayı kuruş"
                          : ""
                  }
                  className="!py-1.5 !text-sm !rounded-lg !px-3"
                  wrapperClassName="!space-y-0"
                />
                <p className="text-[10px] text-gray-500 mt-1 font-medium">
                  = {formatTl(kurus)}
                </p>
                {changed(k) && origOf(k) !== undefined && (
                  <p className="text-[10px] text-primary mt-0.5">
                    Önceki: {Number(origOf(k)) || 0} kuruş ({formatTl(Number(origOf(k)) || 0)})
                  </p>
                )}
              </FieldShell>
            );
          }
          return null;
        })}
      </div>
      <p className="mt-3 text-[11px] text-gray-400">
        Fiyatlar kuruş cinsindendir (100 kuruş = 1 ₺); virgül/ondalık kullanılmaz,
        yalnızca tam sayı kuruş girilir.
      </p>
    </div>
  );
}
